import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import Styles from "./Checkmark.module.scss";

type CheckmarkProps = {
  selected?: boolean;
  name: string;
  value: string | number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  label?: string | React.ReactNode;
  withBorder?: boolean;
  checked?: boolean;
  className?: string;
  autoFocus?: boolean;
  error?: string;
  resetError?: () => void;
  id?: string;
};

const Checkmark = ({
  selected,
  name,
  value,
  onChange,
  label,
  withBorder = false,
  className,
  autoFocus,
  error,
  resetError,
  id,
}: CheckmarkProps) => {
  const [focus, setFocus] = useState(false);
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus) {
      ref.current?.focus();
    }
  }, [autoFocus]);

  const onCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    resetError && resetError();
    onChange(e);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle Space and Enter keys to toggle the checkbox
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();

      // Create a synthetic change event to trigger the onChange handler
      const syntheticEvent = {
        ...e,
        target: {
          ...e.currentTarget,
          checked: !e.currentTarget.checked,
        },
        currentTarget: e.currentTarget,
      } as React.ChangeEvent<HTMLInputElement>;

      onCheck(syntheticEvent);
    }
  };

  const checkboxId = id || `${name}_${String(value)}`;

  return (
    <div
      className={classNames(Styles.checkmark, className, {
        [Styles.withBorder]: withBorder,
        [Styles.focus]: focus,
        [Styles.error]: !!error,
      })}
    >
      <input
        role="checkbox"
        id={checkboxId}
        type="checkbox"
        name={name}
        value={value}
        onChange={onCheck}
        onKeyDown={handleKeyDown}
        defaultChecked={selected}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        ref={ref}
        aria-checked={selected}
        aria-describedby={error ? `${checkboxId}_error` : undefined}
        aria-invalid={!!error}
        tabIndex={0}
      />
      <label className={Styles.basicLabel} htmlFor={checkboxId}>
        {label}
      </label>

      {/* <span className={Styles.text}></span> */}
    </div>
  );
};

export { Checkmark };
